.field {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.label {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.required {
  color: var(--color-danger-fg);
}

.hint {
  font-size: var(--text-sm);
  line-height: var(--text-sm-line);
  color: var(--color-text-secondary);
}

.error {
  display: flex;
  align-items: flex-start;
  gap: var(--space-2);
  font-size: var(--text-sm);
  line-height: var(--text-sm-line);
  color: var(--color-danger-fg);
}

.visuallyHidden {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* --- text controls ----------------------------------------------------- */

.control {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: 40px;
  padding: 0 var(--space-3);
  background: var(--color-surface);
  border: var(--border-width-thin) solid var(--color-border-default);
  border-radius: var(--radius-md);
  transition:
    border-color var(--duration-fast) var(--easing-standard),
    background-color var(--duration-fast) var(--easing-standard),
    box-shadow var(--duration-fast) var(--easing-standard);
}

.control:hover {
  border-color: var(--color-border-strong);
}

.control:focus-within {
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 2px var(--color-focus-halo);
  outline: none;
}

.control:has(input:disabled) {
  background: var(--color-surface-sunken);
  opacity: var(--opacity-disabled);
}

.controlInvalid {
  border-color: var(--color-danger-emphasis);
}

.input {
  flex: 1;
  min-width: 0;
  height: 100%;
  background: none;
  border: 0;
  font-size: var(--text-base);
  color: var(--color-text-primary);
}

.input:focus {
  /* The wrapper carries the ring; a second one on the input would double it. */
  outline: none;
}

.input::placeholder {
  color: var(--color-text-tertiary);
}

/* The engine-drawn clear affordance is removed: it ignores the token palette,
 * differs between Chromium and WebKit, and is unreachable by keyboard. Where a
 * clear control is wanted it is passed as `slotEnd`, which is a real button. */
.input[type="search"]::-webkit-search-cancel-button,
.input[type="search"]::-webkit-search-decoration {
  appearance: none;
}

.adornment {
  display: inline-flex;
  flex: none;
  align-items: center;
  color: var(--color-text-tertiary);
}

.textarea {
  width: 100%;
  min-height: 100px;
  padding: var(--space-3);
  background: var(--color-surface);
  border: var(--border-width-thin) solid var(--color-border-default);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  line-height: var(--text-base-line);
  color: var(--color-text-primary);
  resize: vertical;
  transition:
    border-color var(--duration-fast) var(--easing-standard),
    box-shadow var(--duration-fast) var(--easing-standard);
}

.textarea:hover {
  border-color: var(--color-border-strong);
}

.textarea:focus {
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 2px var(--color-focus-halo);
  outline: none;
}

.textarea::placeholder {
  color: var(--color-text-tertiary);
}

/* --- checkbox and switch ------------------------------------------------ */

.choice {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}

.choiceLabel {
  font-size: var(--text-base);
  color: var(--color-text-primary);
  cursor: pointer;
}

.checkbox {
  display: inline-flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  background: var(--color-surface);
  border: var(--border-width-thin) solid var(--color-border-strong);
  border-radius: var(--radius-sm);
  transition:
    background-color var(--duration-fast) var(--easing-standard),
    border-color var(--duration-fast) var(--easing-standard);
}

.checkbox[data-state="checked"],
.checkbox[data-state="indeterminate"] {
  background: var(--color-accent-emphasis);
  border-color: var(--color-accent-emphasis);
}

.checkboxIndicator {
  display: inline-flex;
  color: var(--color-text-inverse);
}

.switch {
  position: relative;
  flex: none;
  width: 28px;
  height: 16px;
  padding: 0;
  background: var(--color-border-strong);
  border: 0;
  border-radius: var(--radius-full);
  transition: background-color var(--duration-fast) var(--easing-standard);
}

.switch[data-state="checked"] {
  background: var(--color-accent-emphasis);
}

.switchThumb {
  display: block;
  width: 12px;
  height: 12px;
  background: var(--color-surface);
  border-radius: var(--radius-full);
  transform: translateX(2px);
  transition: transform var(--duration-fast) var(--easing-standard);
  will-change: transform;
}

.switchThumb[data-state="checked"] {
  transform: translateX(14px);
}

/* --- select -------------------------------------------------------------- */

.selectTrigger {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
  height: 40px;
  padding: 0 var(--space-3);
  background: var(--color-surface);
  border: var(--border-width-thin) solid var(--color-border-default);
  border-radius: var(--radius-md);
  font-size: var(--text-base);
  color: var(--color-text-primary);
  cursor: pointer;
  transition:
    border-color var(--duration-fast) var(--easing-standard),
    box-shadow var(--duration-fast) var(--easing-standard);
}

.selectTrigger:hover {
  border-color: var(--color-border-strong);
}

.selectTrigger:focus {
  border-color: var(--color-border-focus);
  box-shadow: 0 0 0 2px var(--color-focus-halo);
  outline: none;
}

.selectTrigger[data-placeholder] {
  color: var(--color-text-tertiary);
}

.selectContent {
  z-index: var(--z-dropdown);
  min-width: var(--radix-select-trigger-width);
  max-height: var(--radix-select-content-available-height);
  overflow: hidden;
  background: var(--color-surface-raised);
  border: var(--border-width-thin) solid var(--color-border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-medium);
}

.selectViewport {
  padding: var(--space-1);
}

.selectScroll {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  color: var(--color-text-secondary);
}

.selectItem {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  height: var(--row-height);
  padding: 0 var(--space-3);
  border-radius: var(--radius-sm);
  font-size: var(--text-base);
  color: var(--color-text-primary);
  cursor: pointer;
  user-select: none;
}

.selectItem[data-highlighted] {
  background: var(--color-surface-hover);
  outline: none;
}

.selectItem[data-disabled] {
  color: var(--color-text-tertiary);
  pointer-events: none;
}

.selectItemIndicator {
  display: inline-flex;
  flex: none;
  justify-content: center;
  width: 12px;
  color: var(--color-accent-fg);
}

/* The up arrow is the down arrow turned over, rather than a second glyph.
 * A class rather than an inline `style`, so that the stylesheet audit in
 * tokens.test.ts can see every declaration the components make. */
.selectScrollUp svg {
  transform: rotate(180deg);
}
